import { Tabs, useLocalSearchParams, usePathname, useSegments } from "expo-router";
import { Platform, View } from "react-native";
import { HapticTab } from "@/components/HapticTab";
import TabBarBackground from "@/components/ui/TabBarBackground";
import { useTheme } from "@/hooks/useTheme";
import { ThemedText } from "@/components/ThemedText";
import { MaterialIcons } from "@expo/vector-icons";
import { useEffect, useState } from "react";
import { useSearchParams } from "expo-router/build/hooks";

function ProgressIndicator() {
  const { colors } = useTheme();
  const steps = ["Info", "Documents", "Photo", "Verify"];
  const [currentStep, setCurrentStep] = useState(0);
  const pathname = usePathname();
  const { session: t } = useLocalSearchParams();

  useEffect(() => {
    if (pathname.includes("info")) setCurrentStep(0);
    if (pathname.includes("documents")) setCurrentStep(1);
    if (pathname.includes("photo")) setCurrentStep(2);
    if (pathname.includes("verify")) setCurrentStep(3);
  }, [pathname]);

  return (
    <View
      style={{
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        paddingHorizontal: 20,
        paddingVertical: 12,
        backgroundColor: colors.card,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
      }}
    >
      <ThemedText>{t}</ThemedText>
      {steps.map((step, index) => (
        <View
          key={step}
          style={{
            flex: index < steps.length - 1 ? 1 : undefined,
            alignItems: "center",
            flexDirection: "row",
          }}
        >
          <View
            style={{
              width: 24,
              height: 24,
              borderRadius: 12,
              backgroundColor: index <= currentStep ? colors.primary : colors.border,
              alignItems: "center",
              justifyContent: "center",
              marginRight: 8,
            }}
          >
            <ThemedText
              style={{
                fontSize: 12,
                fontWeight: "600",
                color: index <= currentStep ? "white" : colors.textSecondary,
              }}
            >
              {index + 1}
            </ThemedText>
          </View>
          <ThemedText
            style={{
              fontSize: 12,
              color: index <= currentStep ? colors.primary : colors.textSecondary,
              fontWeight: index === currentStep ? "600" : "400",
            }}
          >
            {step}
          </ThemedText>
          {index < steps.length - 1 && (
            <View
              style={{
                flex: 1,
                height: 2,
                backgroundColor: index < currentStep ? colors.primary : colors.border,
                marginLeft: 8,
              }}
            />
          )}
        </View>
      ))}
    </View>
  );
}

export default function SessionLayout() {
  const { colors } = useTheme();
  const { session } = useLocalSearchParams();

  return (
    <View style={{ flex: 1 }}>
      <ProgressIndicator />
      <Tabs
        layout={({ children }) => (
          <View
            style={{
              flex: 1,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <View
              style={{
                flex: 1,
                width: "100%",
                maxWidth: 800,
              }}
            >
              {children}
            </View>
          </View>
        )}
        screenOptions={{
          animation: "shift",
          tabBarHideOnKeyboard: true,
          sceneStyle: {
            backgroundColor: colors.backgroundSecondary,
            borderColor: colors.border,
          },
          tabBarLabelPosition: Platform.select({
            default: "below-icon",
          }),
          tabBarLabelStyle: Platform.select({
            default: { color: colors.text, fontSize: 12 },
          }),
          tabBarActiveBackgroundColor: colors.primary,
          tabBarActiveTintColor: "white",
          headerShown: false,
          headerShadowVisible: false,
          tabBarBadgeStyle: { maxWidth: 43, top: -6, right: -6 },
          tabBarButton: HapticTab,
          tabBarBackground: TabBarBackground,
          tabBarStyle: { display: "none" },
        }}
      >
        <Tabs.Screen
          name="info"
          options={{
            title: "Info",
            animation: "shift",
            tabBarIcon: ({ color }: any) => <MaterialIcons name="person" size={24} color={color} />,
          }}
        />
        <Tabs.Screen
          name="documents"
          options={{
            title: "Documents",
            animation: "shift",
            tabBarIcon: ({ color }: any) => <MaterialIcons name="description" size={24} color={color} />,
          }}
        />
        <Tabs.Screen
          name="photo"
          options={{
            title: "Photo",
            animation: "shift",
            tabBarIcon: ({ color }: any) => <MaterialIcons name="photo-camera" size={24} color={color} />,
          }}
        />
        <Tabs.Screen
          name="verify"
          options={{
            title: "Verify",
            animation: "shift",
            tabBarIcon: ({ color }: any) => <MaterialIcons name="verified" size={24} color={color} />,
          }}
        />
      </Tabs>
    </View>
  );
}
